/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';

interface LegendItem {
  label: string;
  color?: string;
  valueLabel: string;
}

@Component({
  selector: 'ptl-feedback-results-range-chart',
  templateUrl: './results-range-chart.component.html',
  styleUrls: ['./results-range-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultsRangeChartComponent implements OnChanges {
  @Input() userScore = 0;
  @Input() feedbackProvidersAvgScores: number[] = [];

  avgUserPercent = '0%';
  avgProvidersPercent = '0%';
  providersAverageValue: number | null = null;
  legend: LegendItem[] = [];
  animate = false;
  max = 100;

  private readonly selfRatingColor = '#000000';
  private readonly providersAvgColor = '#5a27e7';

  ngOnChanges(_changes: SimpleChanges): void {
    this.buildViewModel();
  }

  private buildViewModel(): void {
    const userVal = this.normalize(this.userScore);
    this.avgUserPercent = this.toPercentString(userVal, this.max);

    const providerNumbers = (this.feedbackProvidersAvgScores ?? [])
      .map((v) => this.normalize(v))
      .filter((v): v is number => Number.isFinite(v));

    this.providersAverageValue = providerNumbers.length ? providerNumbers.reduce((s, v) => s + v, 0) / providerNumbers.length : null;

    this.avgProvidersPercent = this.toPercentString(this.providersAverageValue, this.max);

    this.legend = [
      { label: 'Self rating', color: this.selfRatingColor, valueLabel: this.toPercentLabel(userVal, this.max) },
      { label: 'Evaluators', color: this.providersAvgColor, valueLabel: this.toPercentLabel(this.providersAverageValue, this.max) },
    ];
  }

  private normalize(v: unknown): number | null {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const parsed = Number(v as string);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toPercentString(value: number | null, max: number | null): string {
    if (!Number.isFinite(value as number) || !Number.isFinite(max as number) || max === 0) return '0%';
    return `${Math.round(((value as number) / (max as number)) * 100)}%`;
  }

  private toPercentLabel(value: number | null, max: number | null): string {
    if (!Number.isFinite(value as number) || !Number.isFinite(max as number) || max === 0) return 'NaN';
    return this.toPercentString(value, max);
  }

  protected readonly Array = Array;
}
